import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Moon, Star, Sparkles, Eye, Shield, Heart, Gem, Users, 
  Compass, Menu, X, Instagram, Mail, Phone, ChevronDown,
  Flame, Droplets, Leaf, Hand, Volume2, VolumeX
} from "lucide-react";

// Instagram Direct Link Base
const INSTAGRAM_USERNAME = "nahyradasondas_tarot";
const getInstagramDM = (message) => `https://ig.me/m/${INSTAGRAM_USERNAME}?text=${encodeURIComponent(message)}`;

// Pre-defined messages
const MESSAGES = {
  agendar: "Olá Nahyra! Gostaria de agendar uma consulta.",
  leitura: "Olá Nahyra! Gostaria de agendar uma leitura.",
  tarotPromo: "Olá Nahyra! Tenho interesse no Jogo de Tarot por R$20,00.",
  contato: "Olá Nahyra! Vim pelo seu site e gostaria de mais informações.",
  // Services
  tarot: "Olá Nahyra! Gostaria de agendar uma consulta de Tarot ou Baralho Cigano.",
  trabalhos: "Olá Nahyra! Tenho interesse em Trabalhos Espirituais.",
  limpeza: "Olá Nahyra! Gostaria de saber mais sobre Limpeza Espiritual.",
  banhos: "Olá Nahyra! Tenho interesse em Banhos Espirituais.",
  benzimento: "Olá Nahyra! Gostaria de agendar um Benzimento.",
  protecao: "Olá Nahyra! Tenho interesse em Proteção Espiritual.",
  amuletos: "Olá Nahyra! Gostaria de saber mais sobre Amuletos e Patuás.",
  ancestralidade: "Olá Nahyra! Tenho interesse em Consulta Ancestral.",
  acompanhamento: "Olá Nahyra! Gostaria de saber mais sobre Acompanhamento Espiritual."
};

// Instagram profile link (for footer)
const INSTAGRAM_PROFILE = "https://www.instagram.com/nahyradasondas_tarot";

// Background Music
const BACKGROUND_MUSIC_URL = "https://customer-assets.emergentagent.com/job_tarot-nahyra-oracle/artifacts/s6hludxu_musica%20bg%20nahyra.mp3";

// Images
const IMAGES = {
  background: "https://customer-assets.emergentagent.com/job_304c2473-0872-4a24-bb23-108b2c628a46/artifacts/nm0sk4q5_background.png",
  nahyra: "https://customer-assets.emergentagent.com/job_304c2473-0872-4a24-bb23-108b2c628a46/artifacts/hyh8po0o_nahyra%20das%20ondas.jpeg",
  tarotBanner: "https://customer-assets.emergentagent.com/job_304c2473-0872-4a24-bb23-108b2c628a46/artifacts/7krx1vsr_jogo%20de%20tarot.jpeg",
  tarot: "https://images.unsplash.com/photo-1636298313382-e10978c241ef?w=600&q=80",
  trabalhos: "https://images.unsplash.com/photo-1748633412469-a045f8015d00?w=600&q=80",
  limpeza: "https://images.unsplash.com/photo-1729697713588-387911504c06?w=600&q=80",
  banhos: "https://images.pexels.com/photos/9154235/pexels-photo-9154235.jpeg?auto=compress&w=600",
  benzimento: "https://images.unsplash.com/photo-1690329284368-ac2507912c60?w=600&q=80",
  protecao: "https://images.unsplash.com/photo-1742402983116-190c24ecc181?w=600&q=80",
  amuletos: "https://images.unsplash.com/photo-1746379025496-3c7e4c562ab5?w=600&q=80",
  ancestralidade: "https://images.pexels.com/photos/34768710/pexels-photo-34768710.jpeg?auto=compress&w=600",
  acompanhamento: "https://images.pexels.com/photos/6871033/pexels-photo-6871033.jpeg?auto=compress&w=600"
};

// Services Data
const SERVICES = [
  {
    id: "tarot",
    title: "Tarot e Baralho Cigano",
    icon: Eye,
    image: IMAGES.tarot,
    description: "Revelações do passado, presente e futuro através das cartas sagradas.",
    items: [
      "Consulta de tarot online",
      "Baralho cigano completo",
      "Amor e relacionamentos",
      "Trabalho e dinheiro",
      "Pergunta objetiva",
      "Consulta geral de vida"
    ]
  },
  {
    id: "trabalhos",
    title: "Trabalhos Espirituais",
    icon: Flame,
    image: IMAGES.trabalhos,
    description: "Rituais ancestrais para transformar sua realidade.",
    items: [
      "Abertura de caminhos",
      "Prosperidade financeira",
      "Proteção espiritual",
      "Fortalecimento pessoal",
      "Trabalho personalizado"
    ]
  },
  {
    id: "limpeza",
    title: "Limpeza Espiritual",
    icon: Leaf,
    image: IMAGES.limpeza,
    description: "Purificação profunda da aura e energia pessoal.",
    items: [
      "Limpeza energética profunda",
      "Quebra de inveja e mau-olhado",
      "Descarrego espiritual",
      "Corte de laços energéticos",
      "Limpeza para casa"
    ]
  },
  {
    id: "banhos",
    title: "Banhos Espirituais",
    icon: Droplets,
    image: IMAGES.banhos,
    description: "Banhos preparados com ervas sagradas para renovação.",
    items: [
      "Banho de descarrego",
      "Banho de prosperidade",
      "Banho de amor",
      "Banho de proteção",
      "Banhos personalizados",
      "Kits de banho espiritual"
    ]
  },
  {
    id: "benzimento",
    title: "Benzimento",
    icon: Hand,
    image: IMAGES.benzimento,
    description: "Orações e bênçãos ancestrais para cura e proteção.",
    items: [
      "Benzimento contra mau-olhado",
      "Benzimento para ansiedade",
      "Benzimento para crianças",
      "Reza de proteção",
      "Cura espiritual"
    ]
  },
  {
    id: "protecao",
    title: "Proteção Espiritual",
    icon: Shield,
    image: IMAGES.protecao,
    description: "Escudos energéticos contra negatividade e inveja.",
    items: [
      "Proteção pessoal",
      "Proteção contra inveja",
      "Escudo espiritual",
      "Proteção para casa",
      "Selamento energético"
    ]
  },
  {
    id: "amuletos",
    title: "Amuletos e Patuás",
    icon: Gem,
    image: IMAGES.amuletos,
    description: "Objetos consagrados carregados de energia e intenção.",
    items: [
      "Patuá de proteção",
      "Amuleto da sorte",
      "Talismã de prosperidade",
      "Amuleto amoroso",
      "Objetos consagrados"
    ]
  },
  {
    id: "ancestralidade",
    title: "Ancestralidade",
    icon: Users,
    image: IMAGES.ancestralidade,
    description: "Conexão com a sabedoria dos seus antepassados.",
    items: [
      "Consulta ancestral",
      "Cura ancestral",
      "Linhagem familiar",
      "Altar ancestral",
      "Reconexão espiritual"
    ]
  },
  {
    id: "acompanhamento",
    title: "Acompanhamento Espiritual",
    icon: Compass,
    image: IMAGES.acompanhamento,
    description: "Orientação contínua para seu desenvolvimento espiritual.",
    items: [
      "Orientação espiritual",
      "Atendimento mensal",
      "Direcionamento de vida",
      "Desenvolvimento intuitivo"
    ]
  }
];

// Navigation Items
const NAV_ITEMS = [
  { id: "sobre", label: "Sobre" },
  { id: "tarot", label: "Tarot" },
  { id: "servicos", label: "Serviços" },
  { id: "contato", label: "Contato" }
];

// Stars Component
const Stars = () => {
  const stars = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    top: Math.random() * 100,
    size: Math.random() * 2 + 1,
    delay: Math.random() * 5
  }));

  return (
    <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
      {stars.map((star) => (
        <div
          key={star.id}
          className="star animate-twinkle"
          style={{
            left: `${star.left}%`,
            top: `${star.top}%`,
            width: `${star.size}px`,
            height: `${star.size}px`,
            animationDelay: `${star.delay}s`
          }}
        />
      ))}
    </div>
  );
};

// Navigation Component
const Navigation = ({ activeSection }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setMobileMenuOpen(false);
    }
  };

  return (
    <>
      <nav
        data-testid="navigation"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? "bg-black/80 backdrop-blur-lg border-b border-white/5" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <button
              onClick={() => scrollToSection("hero")}
              className="flex items-center gap-2 cursor-pointer"
              data-testid="logo"
            >
              <Moon className="w-6 h-6 text-[#00CED1]" />
              <span className="font-cinzel text-lg md:text-xl text-white tracking-wider">
                Nahyra das Ondas
              </span>
            </button>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              {NAV_ITEMS.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="nav-link"
                  data-testid={`nav-${item.id}`}
                >
                  {item.label}
                </button>
              ))}
              <a
                href={getInstagramDM(MESSAGES.agendar)}
                target="_blank"
                rel="noopener noreferrer"
                className="glow-button"
                data-testid="nav-cta"
              >
                Agendar
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-white p-2"
              onClick={() => setMobileMenuOpen(true)}
              data-testid="mobile-menu-button"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="mobile-menu"
            data-testid="mobile-menu"
          >
            <button
              className="absolute top-4 right-4 text-white p-2"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="close-mobile-menu"
            >
              <X className="w-8 h-8" />
            </button>
            
            <Moon className="w-12 h-12 text-[#00CED1] mb-8" />
            
            {NAV_ITEMS.map((item, index) => (
              <motion.button
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                onClick={() => scrollToSection(item.id)}
                className="text-2xl font-cormorant text-white tracking-widest"
                data-testid={`mobile-nav-${item.id}`}
              >
                {item.label}
              </motion.button>
            ))}
            
            <motion.a
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              href={getInstagramDM(MESSAGES.agendar)}
              target="_blank"
              rel="noopener noreferrer"
              className="glow-button mt-4"
              data-testid="mobile-cta"
            >
              Agendar Consulta
            </motion.a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center pt-20" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center lg:text-left"
          >
            <div className="flex items-center justify-center lg:justify-start gap-2 mb-6">
              <Sparkles className="w-5 h-5 text-[#00CED1]" />
              <span className="text-[#00CED1] uppercase tracking-[0.3em] text-xs">
                Leituras Oraculares
              </span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight mb-6">
              Conexão <span className="text-[#00CED1]">Ancestral</span><br />
              e Clareza para seu <span className="italic">Destino</span>
            </h1>
            
            <p className="text-base lg:text-lg text-slate-300 leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0">
              Sabedoria ancestral através das cartas e rituais sagrados. 
              Conexão com ciclos, sombras e o caminho do seu destino.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <a
                href={getInstagramDM(MESSAGES.leitura)}
                target="_blank"
                rel="noopener noreferrer"
                className="glow-button"
                data-testid="hero-cta"
              >
                Agendar Leitura
              </a>
              <button
                onClick={() => document.getElementById("sobre").scrollIntoView({ behavior: "smooth" })}
                className="flex items-center justify-center gap-2 text-slate-300 hover:text-[#00CED1] transition-colors"
                data-testid="hero-learn-more"
              >
                Conhecer mais
                <ChevronDown className="w-4 h-4 animate-bounce" />
              </button>
            </div>
          </motion.div>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="relative mx-auto w-72 h-72 sm:w-80 sm:h-80 lg:w-96 lg:h-96">
              {/* Glow Effect */}
              <div className="absolute inset-0 rounded-full bg-[#00CED1]/20 blur-3xl animate-pulse" />
              
              {/* Image Container */}
              <div className="relative rounded-full overflow-hidden border-2 border-[#00CED1]/30 shadow-[0_0_60px_rgba(0,206,209,0.3)]">
                <img
                  src={IMAGES.nahyra}
                  alt="Nahyra das Ondas - Tarotista"
                  className="w-full h-full object-cover"
                  data-testid="nahyra-photo"
                />
              </div>
              
              {/* Decorative Elements */}
              <div className="absolute -top-4 -right-4 w-8 h-8 text-[#00CED1] animate-float">
                <Star className="w-full h-full" />
              </div>
              <div className="absolute -bottom-2 -left-2 w-6 h-6 text-[#00CED1] animate-float" style={{ animationDelay: "1s" }}>
                <Moon className="w-full h-full" />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="sobre" className="relative py-20 lg:py-32" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="glass-card p-8 lg:p-12"
        >
          <div className="flex items-center gap-3 mb-6">
            <Moon className="w-6 h-6 text-[#00CED1]" />
            <h2 className="text-3xl lg:text-4xl font-semibold text-white">
              Sobre a Bruxa
            </h2>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <p className="text-slate-300 leading-relaxed mb-4">
                <span className="text-[#00CED1] font-semibold">Nahyra das Ondas</span> é guardiã dos mistérios ancestrais, 
                com mais de <span className="text-[#00CED1] font-semibold">13 anos</span> dedicados ao estudo e prática 
                das artes divinatórias e trabalhos espirituais.
              </p>
              <p className="text-slate-300 leading-relaxed mb-4">
                Iniciada nos caminhos do tarot e do baralho cigano desde jovem, desenvolveu uma conexão profunda 
                com as energias sutis e os ciclos da natureza. Sua prática une a tradição das benzedeiras 
                brasileiras com a sabedoria dos oráculos ciganos.
              </p>
              <p className="text-slate-300 leading-relaxed">
                Especializada em leituras oraculares, trabalhos de abertura de caminhos, proteção espiritual 
                e cura ancestral, Nahyra guia seus consulentes através das sombras rumo à luz do autoconhecimento.
              </p>
            </div>
            
            <div className="flex flex-col gap-4">
              <div className="glass-card p-4 text-center">
                <p className="text-4xl font-bold text-[#00CED1]">13+</p>
                <p className="text-sm text-slate-400 uppercase tracking-wider">Anos de Experiência</p>
              </div>
              <div className="glass-card p-4 text-center">
                <p className="text-4xl font-bold text-[#00CED1]">5000+</p>
                <p className="text-sm text-slate-400 uppercase tracking-wider">Consultas Realizadas</p>
              </div>
              <div className="glass-card p-4 text-center">
                <p className="text-4xl font-bold text-[#00CED1]">9</p>
                <p className="text-sm text-slate-400 uppercase tracking-wider">Especialidades</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Promo Banner Section
const PromoBanner = () => {
  return (
    <section id="tarot" className="relative py-16 lg:py-24" data-testid="promo-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          {/* Section Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Star className="w-5 h-5 text-[#00CED1]" />
              <span className="text-[#00CED1] uppercase tracking-[0.3em] text-xs">
                Promoção Especial
              </span>
              <Star className="w-5 h-5 text-[#00CED1]" />
            </div>
            <h2 className="text-3xl lg:text-4xl font-semibold text-white mb-4">
              Jogo de Tarot Promocional
            </h2>
            <p className="text-slate-300 max-w-xl mx-auto">
              Aproveite esta oportunidade única para receber orientações do destino 
              com uma leitura intuitiva e direta.
            </p>
          </div>

          {/* Banner Card */}
          <a
            href={getInstagramDM(MESSAGES.tarotPromo)}
            target="_blank"
            rel="noopener noreferrer"
            className="block promo-banner rounded-2xl overflow-hidden"
            data-testid="promo-banner"
          >
            <div className="relative">
              <img
                src={IMAGES.tarotBanner}
                alt="Jogo de Tarot - 3 perguntas por R$20,00"
                className="w-full h-auto"
                data-testid="tarot-banner-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            </div>
          </a>

          {/* CTA Button */}
          <div className="text-center mt-8">
            <a
              href={getInstagramDM(MESSAGES.tarotPromo)}
              target="_blank"
              rel="noopener noreferrer"
              className="glow-button animate-pulse-glow inline-flex items-center gap-3"
              data-testid="promo-cta"
            >
              <Phone className="w-5 h-5" />
              Quero minha leitura
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Service Card Component
const ServiceCard = ({ service, index }) => {
  const Icon = service.icon;
  const serviceMessage = MESSAGES[service.id] || MESSAGES.contato;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="service-card"
      data-testid={`service-card-${service.id}`}
    >
      {/* Image */}
      <div className="service-card-image">
        <img
          src={service.image}
          alt={service.title}
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        
        {/* Icon Badge */}
        <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm border border-[#00CED1]/30 flex items-center justify-center">
          <Icon className="w-5 h-5 text-[#00CED1]" />
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className="text-xl lg:text-2xl font-semibold text-white mb-2">
          {service.title}
        </h3>
        <p className="text-slate-400 text-sm mb-4">
          {service.description}
        </p>

        {/* Items List */}
        <ul className="space-y-2 mb-6">
          {service.items.slice(0, 4).map((item, idx) => (
            <li key={idx} className="flex items-center gap-2 text-sm text-slate-300">
              <span className="w-1 h-1 rounded-full bg-[#00CED1]" />
              {item}
            </li>
          ))}
          {service.items.length > 4 && (
            <li className="text-xs text-[#00CED1]">
              + {service.items.length - 4} mais...
            </li>
          )}
        </ul>

        {/* CTA */}
        <a
          href={getInstagramDM(serviceMessage)}
          target="_blank"
          rel="noopener noreferrer"
          className="glow-button w-full text-center block"
          data-testid={`service-cta-${service.id}`}
        >
          Consultar
        </a>
      </div>
    </motion.div>
  );
};

// Services Section
const ServicesSection = () => {
  return (
    <section id="servicos" className="relative py-20 lg:py-32" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-[#00CED1]" />
            <span className="text-[#00CED1] uppercase tracking-[0.3em] text-xs">
              Nossos Serviços
            </span>
            <Sparkles className="w-5 h-5 text-[#00CED1]" />
          </div>
          <h2 className="text-3xl lg:text-5xl font-semibold text-white mb-4">
            Caminhos para a <span className="text-[#00CED1]">Transformação</span>
          </h2>
          <p className="text-slate-300 max-w-2xl mx-auto">
            Descubra nossos serviços espirituais e encontre o caminho que ressoa com sua alma.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {SERVICES.map((service, index) => (
            <ServiceCard key={service.id} service={service} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

// Footer Section
const Footer = () => {
  return (
    <footer id="contato" className="relative py-16 border-t border-white/5" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          {/* Logo */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <Moon className="w-8 h-8 text-[#00CED1]" />
            <span className="font-cinzel text-2xl text-white tracking-wider">
              Nahyra das Ondas
            </span>
          </div>

          {/* Tagline */}
          <p className="text-slate-400 mb-8 max-w-md mx-auto">
            Leituras oraculares & sabedoria ancestral.<br />
            Conexão com ciclos, sombras e destino.
          </p>

          {/* Social Links */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <a
              href={INSTAGRAM_PROFILE}
              target="_blank"
              rel="noopener noreferrer"
              className="social-link"
              data-testid="footer-instagram"
            >
              <Instagram className="w-5 h-5" />
            </a>
            <a
              href="mailto:contato@nahyradasondastarot.com.br"
              className="social-link"
              data-testid="footer-email"
            >
              <Mail className="w-5 h-5" />
            </a>
            <a
              href={getInstagramDM(MESSAGES.contato)}
              target="_blank"
              rel="noopener noreferrer"
              className="social-link"
              data-testid="footer-dm"
            >
              <Phone className="w-5 h-5" />
            </a>
          </div>

          {/* Contact Info */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 text-sm text-slate-400 mb-8">
            <a
              href={INSTAGRAM_PROFILE}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#00CED1] transition-colors"
            >
              @nahyradasondas_tarot
            </a>
            <span className="hidden sm:block">•</span>
            <a
              href="mailto:contato@nahyradasondastarot.com.br"
              className="hover:text-[#00CED1] transition-colors"
            >
              contato@nahyradasondastarot.com.br
            </a>
          </div>

          {/* Copyright */}
          <p className="text-xs text-slate-500">
            © {new Date().getFullYear()} Nahyra das Ondas Tarot. Todos os direitos reservados.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Instagram Float Button
const InstagramFloat = () => {
  return (
    <a
      href={getInstagramDM(MESSAGES.contato)}
      target="_blank"
      rel="noopener noreferrer"
      className="instagram-float"
      data-testid="instagram-float"
    >
      <Instagram className="w-7 h-7 text-white" />
    </a>
  );
};

// Background Music Component
const BackgroundMusic = () => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  useEffect(() => {
    const handleFirstInteraction = () => {
      if (!hasInteracted && audioRef.current) {
        setHasInteracted(true);
        audioRef.current.volume = 0.3;
        audioRef.current.play()
          .then(() => setIsPlaying(true))
          .catch(err => console.log("Autoplay blocked:", err));
      }
    };

    // Listen for any user interaction
    const events = ['click', 'touchstart', 'keydown', 'scroll'];
    events.forEach(event => {
      document.addEventListener(event, handleFirstInteraction, { once: true });
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleFirstInteraction);
      });
    };
  }, [hasInteracted]);

  const toggleMusic = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  return (
    <>
      <audio
        ref={audioRef}
        src={BACKGROUND_MUSIC_URL}
        loop
        preload="auto"
      />
      <button
        onClick={toggleMusic}
        className="music-toggle"
        data-testid="music-toggle"
        aria-label={isPlaying ? "Pausar música" : "Tocar música"}
      >
        {isPlaying ? (
          <Volume2 className="w-5 h-5 text-[#00CED1]" />
        ) : (
          <VolumeX className="w-5 h-5 text-slate-400" />
        )}
      </button>
    </>
  );
};

// Main App Component
function App() {
  return (
    <div className="relative min-h-screen">
      {/* Background */}
      <div className="hero-bg" />
      <div className="hero-overlay" />
      
      {/* Stars */}
      <Stars />
      
      {/* Navigation */}
      <Navigation />
      
      {/* Main Content */}
      <main className="relative z-10">
        <HeroSection />
        <AboutSection />
        <PromoBanner />
        <ServicesSection />
        <Footer />
      </main>
      
      {/* Instagram Float */}
      <InstagramFloat />
      
      {/* Background Music */}
      <BackgroundMusic />
    </div>
  );
}

export default App;
